/**
 * Board Self-Healing Hook  (v2 — board-only recovery)
 *
 * Recovery Ladder (all board-only until last resort):
 *   Step 1 – REFRESH_DATA:      force REST refetch + reset pagination
 *   Step 2 – RESUBSCRIBE_SOCKET: disconnect + reconnect socket, re-register listeners
 *   Step 3 – REMOUNT:           increment boardKey / renderEpoch for board subtree only
 *   Step 4 – STABLE_FALLBACK:   show cached last-known-good flights for 5 min, then retry
 *   Step 5 – ROUTE_RELOAD:      window.location.reload() (LAST RESORT, kills ads/ticker)
 *
 * Guardrails:
 *   - 60 s cooldown between recovery attempts
 *   - After 5 recoveries/hour → enter stable-fallback for 5 min, then retry
 *   - Blank detection uses useBoardHealthProbe results (MutationObserver + periodic)
 *   - "Empty schedule" (0 flights after filter) is treated as HEALTHY, not blank
 *   - NETWORK_STALE issues never escalate to ROUTE_RELOAD (reload can't fix network)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { checkUIHealth } from '../services/uiHealthMonitor.js';
import {
  getComprehensiveStatus,
  updateDomDataRowCount,
  BlankReason
} from '../services/flightDataMonitor.js';
import {
  storeLastKnownGood,
  getLastKnownGood
} from '../services/lastKnownGoodStore.js';
import {
  RecoveryAction,
  BoardBlankReason,
  recordRecoveryAttempt,
  setStableFallbackUntil,
  setLastError,
  getRecoveryCount1h,
  getLastRecoveryAt,
  isInStableFallback
} from '../services/boardHealthStore.js';

// ── Timing constants ────────────────────────────────────────────────────────
const PROBE_CHECK_INTERVAL_MS = 5000;     // 5 s between health checks
const STABILITY_WINDOW_MS     = 10000;    // 10 s healthy before hiding fallback
const FALLBACK_SHOW_DELAY_MS  = 5000;     // 5 s blank before showing fallback
const RECOVERY_COOLDOWN_MS    = 60000;    // 60 s between recovery attempts
const STABLE_FALLBACK_DURATION_MS = 5 * 60 * 1000; // 5 min
const MAX_RECOVERIES_PER_HOUR = 5;

// Delay before each recovery level fires (measured from blank detection)
const LEVEL_DELAYS_MS = [
  5000,    // Step 1: REFRESH_DATA
  30000,   // Step 2: RESUBSCRIBE_SOCKET
  60000,   // Step 3: REMOUNT
  120000,  // Step 4: STABLE_FALLBACK
  300000,  // Step 5: ROUTE_RELOAD (5 min, only after stable-fallback expires)
];

// Network-down quiet retry interval (refetch + socket reconnect only, no escalation)
const NETWORK_RETRY_INTERVAL_MS = 60000; // 60 s between quiet retries when network is down

// Reload rate limit (same as before, persisted in localStorage)
const MAX_RELOADS_PER_HOUR = 5;
const RELOAD_WINDOW_MS = 60 * 60 * 1000;
const RELOAD_ATTEMPTS_KEY = 'fids_reload_attempts';

function loadAttempts(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const a = JSON.parse(raw);
    const cutoff = Date.now() - RELOAD_WINDOW_MS;
    return a.filter(ts => ts > cutoff);
  } catch { return []; }
}
function saveAttempts(key, a) { try { localStorage.setItem(key, JSON.stringify(a)); } catch {} }
function canReload() { return loadAttempts(RELOAD_ATTEMPTS_KEY).length < MAX_RELOADS_PER_HOUR; }
function recordReload() {
  const a = loadAttempts(RELOAD_ATTEMPTS_KEY);
  a.push(Date.now());
  saveAttempts(RELOAD_ATTEMPTS_KEY, a);
}
function getReloadCount1h() { return loadAttempts(RELOAD_ATTEMPTS_KEY).length; }

/**
 * Check if the current blank state is caused by a network issue (not a UI bug).
 * Page reloads cannot fix network problems — the system should stay in fallback
 * and retry connections quietly until the server becomes reachable.
 */
function isNetworkDown() {
  const status = getComprehensiveStatus(0);
  // Network is down if: data is stale/never received AND socket is disconnected
  return (status.blankReason === BlankReason.NETWORK_STALE ||
          status.blankReason === BlankReason.EMPTY) &&
         !status.socketConnected;
}

// ── Blank-reason categories (kept for backward compat) ──────────────────────
export const BlankCategory = {
  LOADING: 'LOADING',
  EMPTY_API: 'EMPTY_API',
  EMPTY_FILTERED: 'EMPTY_FILTERED',
  DOM_HIDDEN: 'DOM_HIDDEN',
  ZERO_SIZE: 'ZERO_SIZE',
  NOT_PAINTED: 'NOT_PAINTED',
  UNKNOWN: 'UNKNOWN'
};

// ── Diagnostic helpers (unchanged from v1) ──────────────────────────────────
function collectBlankDiagnostics(boardType, flightsReceived, flightsDisplayed, routeMountedAt) {
  const now = Date.now();
  let domRowCount = 0, tableRect = null, tableComputedStyle = null, containerRect = null;
  try {
    const table = document.querySelector('table');
    if (table) {
      domRowCount = table.querySelectorAll('tr[class*="data-row"]').length;
      const r = table.getBoundingClientRect();
      tableRect = { width: r.width, height: r.height };
      const s = window.getComputedStyle(table);
      tableComputedStyle = { display: s.display, visibility: s.visibility, opacity: s.opacity };
    }
    const c = document.querySelector('.striped-container');
    if (c) { const cr = c.getBoundingClientRect(); containerRect = { width: cr.width, height: cr.height }; }
  } catch {}

  let category = BlankCategory.UNKNOWN;
  const ms = routeMountedAt ? (now - routeMountedAt) : null;
  if (ms && ms < 5000) category = BlankCategory.LOADING;
  else if (flightsReceived === 0) category = BlankCategory.EMPTY_API;
  else if (flightsReceived > 0 && flightsDisplayed === 0) category = BlankCategory.EMPTY_FILTERED;
  else if (flightsDisplayed > 0 && domRowCount === 0) {
    if (!tableRect || tableRect.width === 0 || tableRect.height === 0) category = BlankCategory.ZERO_SIZE;
    else if (tableComputedStyle?.visibility === 'hidden' || tableComputedStyle?.display === 'none') category = BlankCategory.DOM_HIDDEN;
    else category = BlankCategory.NOT_PAINTED;
  }

  return { category, boardType, timestamp: new Date().toISOString(),
    counts: { flightsReceived, flightsDisplayed, domRowCount },
    visibility: { tableRect, tableComputedStyle, containerRect },
    timing: { msSinceRouteMounted: ms }
  };
}

function logBlankDiagnostics(d) {
  console.warn(
    `🚨 [BLANK_BOARD] ${d.category} | ${d.boardType} | ` +
    `flights: ${d.counts.flightsReceived}→${d.counts.flightsDisplayed}→${d.counts.domRowCount} | ` +
    `size: ${d.visibility.tableRect?.width ?? 0}x${d.visibility.tableRect?.height ?? 0} | ` +
    `css: ${d.visibility.tableComputedStyle?.display}/${d.visibility.tableComputedStyle?.visibility}`,
    d
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main hook
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * @param {Object}   opts
 * @param {string}   opts.boardType          – 'arrivals' | 'departures'
 * @param {Array}    opts.currentFlights     – flights after filtering
 * @param {Function} opts.onRefetch          – trigger REST refetch
 * @param {Function} opts.onResetPagination  – reset AutoSwipePanels to page 0
 * @param {Function} [opts.onReconnectSocket]– disconnect+reconnect socket
 * @param {Object}   [opts.probeResult]      – latest useBoardHealthProbe output
 */
export function useBoardSelfHealing({
  boardType,
  currentFlights = [],
  onRefetch,
  onResetPagination,
  onReconnectSocket,
  probeResult,
}) {
  // ── State ───────────────────────────────────────────────────────────────
  const [showFallback, setShowFallback]     = useState(false);
  const [fallbackReason, setFallbackReason] = useState(BlankReason.NONE);
  const [recoveryLevel, setRecoveryLevel]   = useState(0);
  const [renderEpoch, setRenderEpoch]       = useState(0);
  const [lastKnownGood, setLastKnownGood]   = useState(null);

  // ── Refs ────────────────────────────────────────────────────────────────
  const blankDetectedAt     = useRef(null);
  const lastHealthyAt       = useRef(Date.now());
  const routeMountedAt      = useRef(Date.now());
  const recoveryTimerRef    = useRef(null);
  const stabilityTimerRef   = useRef(null);
  const isRecovering        = useRef(false);
  const lastDiagnosticsAt   = useRef(0);

  // ── Helpers ─────────────────────────────────────────────────────────────

  /** Is recovery on cooldown? (60 s since last attempt) */
  const isOnCooldown = useCallback(() => {
    const last = getLastRecoveryAt();
    return last != null && (Date.now() - last < RECOVERY_COOLDOWN_MS);
  }, []);

  /** Should we enter stable-fallback mode? (>= 5 recoveries in 1 h) */
  const shouldEnterStableFallback = useCallback(() => {
    return getRecoveryCount1h() >= MAX_RECOVERIES_PER_HOUR;
  }, []);

  /**
   * Determine if the board is actually blank (needs recovery).
   * Uses probe result when available; falls back to DOM query.
   * CRITICAL: 0 flights after filtering is NOT blank — it's an empty schedule.
   * CRITICAL: FALLBACK_LATCHED is NOT blank — the fallback is intentionally showing.
   *           Treating it as blank creates a feedback loop where the fallback can never
   *           be hidden (because data rows aren't in DOM while fallback is rendered).
   */
  const isBoardBlank = useCallback(() => {
    // If probe says healthy, trust it
    if (probeResult) {
      if (probeResult.boardRenderOk) return false;
      // Probe says unhealthy — but if reason is EMPTY_SCHEDULE, that's not a failure
      if (probeResult.boardBlankReason === BoardBlankReason.EMPTY_SCHEDULE) return false;
      // FALLBACK_LATCHED means the fallback is currently rendering (by design).
      // Data rows won't be in the DOM while fallback shows. Don't treat this as blank —
      // let the recovery mechanism check data availability and hide the fallback.
      if (probeResult.boardBlankReason === BoardBlankReason.FALLBACK_LATCHED) return false;
      return true;
    }

    // Fallback: legacy DOM check
    try {
      const table = document.querySelector('table');
      if (!table) {
        // No table in DOM — but if fallback is showing, that's expected
        const fallback = document.querySelector('.board-fallback');
        if (fallback) return false; // Fallback is rendering, not blank
        return true;
      }
      const dataRows = table.querySelectorAll('tr[class*="data-row"]');

      // Check for explicit empty-state marker
      const emptyState = document.querySelector('.board-empty-state');
      if (emptyState) return false;

      if (dataRows.length === 0) {
        // 0 rendered rows — but is this because there are 0 flights?
        if (currentFlights.length === 0) return false; // legitimate empty
        return true; // data exists but not rendered => blank
      }
      return false;
    } catch { return true; }
  }, [probeResult, currentFlights.length]);

  // ── Network-down quiet retry ────────────────────────────────────────────
  /**
   * When network is down, stop escalating and just retry refetch + socket reconnect
   * every 60s until connectivity returns. No page reloads, no remounts.
   */
  const scheduleNetworkRetry = useCallback(() => {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
    recoveryTimerRef.current = setTimeout(() => {
      // Check if network came back
      if (!isNetworkDown()) {
        console.log('✅ [BOARD_RECOVERY] Network appears restored — restarting recovery from step 1');
        setRecoveryLevel(1);
        isRecovering.current = false;
        return; // Let the periodic health check pick it up and start fresh
      }

      console.log('🔄 [BOARD_RECOVERY] Network still down — quiet retry (refetch + socket)');
      if (onReconnectSocket) onReconnectSocket();
      if (onRefetch) onRefetch();

      // Schedule next quiet retry
      scheduleNetworkRetry();
    }, NETWORK_RETRY_INTERVAL_MS);
  }, [onRefetch, onReconnectSocket]);

  // ── Recovery actions ────────────────────────────────────────────────────

  const executeRecovery = useCallback((step) => {
    // Cooldown guard
    if (isOnCooldown()) {
      const remaining = RECOVERY_COOLDOWN_MS - (Date.now() - getLastRecoveryAt());
      console.log(`⏳ [BOARD_RECOVERY] cooldown ${Math.ceil(remaining / 1000)}s remaining, deferring step=${step}`);
      return;
    }

    // Stable-fallback gate
    if (shouldEnterStableFallback() && step < 4) {
      console.log('[BOARD_RECOVERY] Too many recoveries — jumping to STABLE_FALLBACK');
      step = 4; // force stable-fallback
      setRecoveryLevel(4);
    }

    const reason = probeResult?.boardBlankReason || 'UNKNOWN';

    switch (step) {
      case 1: { // REFRESH_DATA
        console.log(`🔄 [BOARD_RECOVERY] step=REFRESH_DATA reason=${reason}`);
        recordRecoveryAttempt(RecoveryAction.REFRESH_DATA);
        if (onResetPagination) onResetPagination();
        if (onRefetch) onRefetch();
        break;
      }

      case 2: { // RESUBSCRIBE_SOCKET
        console.log(`🔄 [BOARD_RECOVERY] step=RESUBSCRIBE_SOCKET reason=${reason}`);
        recordRecoveryAttempt(RecoveryAction.RESUBSCRIBE_SOCKET);
        if (onReconnectSocket) {
          onReconnectSocket();
        } else {
          // Fallback: just refetch if socket reconnect not provided
          if (onRefetch) onRefetch();
        }
        break;
      }

      case 3: { // REMOUNT (board-only)
        // Skip REMOUNT for network issues — remounting won't fix connectivity
        if (isNetworkDown()) {
          console.log(`⏭️ [BOARD_RECOVERY] step=REMOUNT skipped (network down), jumping to STABLE_FALLBACK`);
          setRecoveryLevel(4);
          executeRecovery(4);
          return;
        }
        console.log(`🔄 [BOARD_RECOVERY] step=REMOUNT reason=${reason}`);
        recordRecoveryAttempt(RecoveryAction.REMOUNT);
        setRenderEpoch(e => e + 1);
        if (onRefetch) onRefetch();
        break;
      }

      case 4: { // STABLE_FALLBACK
        console.log(`🔄 [BOARD_RECOVERY] step=STABLE_FALLBACK reason=${reason} duration=5min`);
        recordRecoveryAttempt(RecoveryAction.STABLE_FALLBACK);
        const until = Date.now() + STABLE_FALLBACK_DURATION_MS;
        setStableFallbackUntil(until);

        // Load cached flights for fallback display
        if (!lastKnownGood) {
          const cached = getLastKnownGood(boardType);
          if (cached && cached.flights.length > 0) {
            setLastKnownGood(cached);
          }
        }

        setShowFallback(true);
        setFallbackReason(isNetworkDown() ? BlankReason.NETWORK_STALE : BlankReason.BROKEN_UI);

        // Schedule what happens after stable-fallback expires
        if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = setTimeout(() => {
          setStableFallbackUntil(null);

          // If network is still down, DON'T restart recovery ladder — just do quiet retries
          if (isNetworkDown()) {
            console.log('[BOARD_RECOVERY] Stable-fallback expired but network still down — quiet retries only');
            scheduleNetworkRetry();
            return;
          }

          console.log('[BOARD_RECOVERY] Stable-fallback expired, retrying from step 1');
          setRecoveryLevel(1);
          isRecovering.current = false;
          executeRecovery(1);
        }, STABLE_FALLBACK_DURATION_MS);
        return; // Don't schedule next level — we're waiting
      }

      case 5: { // ROUTE_RELOAD (absolute last resort)
        // NEVER reload for network issues — reloading cannot fix connectivity
        if (isNetworkDown()) {
          console.warn('⚠️ [BOARD_RECOVERY] ROUTE_RELOAD skipped (network down) — quiet retries only');
          scheduleNetworkRetry();
          return;
        }
        if (!canReload()) {
          console.warn('⚠️ [BOARD_RECOVERY] ROUTE_RELOAD rate-limited, staying on fallback');
          return;
        }
        console.log(`🔄 [BOARD_RECOVERY] step=ROUTE_RELOAD reason=${reason} (LAST RESORT)`);
        recordRecoveryAttempt(RecoveryAction.ROUTE_RELOAD);
        recordReload();
        setTimeout(() => window.location.reload(), 100);
        return;
      }

      default:
        console.log('[BOARD_RECOVERY] All steps exhausted, staying on fallback with quiet retries');
        scheduleNetworkRetry();
        return;
    }

    // Schedule next level
    scheduleNextLevel(step);
  }, [
    boardType, showFallback, lastKnownGood, probeResult,
    isOnCooldown, shouldEnterStableFallback, scheduleNetworkRetry,
    onRefetch, onResetPagination, onReconnectSocket
  ]);

  /** Schedule escalation to next recovery step */
  const scheduleNextLevel = useCallback((currentStep) => {
    if (recoveryTimerRef.current) clearTimeout(recoveryTimerRef.current);

    const nextStep = currentStep + 1;
    if (nextStep > 5) {
      // All steps exhausted — quiet retry (includes socket reconnect)
      recoveryTimerRef.current = setTimeout(() => {
        if (showFallback) {
          console.log('🔄 [BOARD_RECOVERY] Background quiet retry (refetch + socket)');
          if (onReconnectSocket) onReconnectSocket();
          if (onRefetch) onRefetch();
        }
      }, NETWORK_RETRY_INTERVAL_MS);
      return;
    }

    // Delay = max(level delay, cooldown remaining)
    const levelDelay = LEVEL_DELAYS_MS[nextStep - 1] || 60000;
    const cooldownRemaining = Math.max(0, RECOVERY_COOLDOWN_MS - (Date.now() - (getLastRecoveryAt() || 0)));
    const delay = Math.max(levelDelay, cooldownRemaining);

    console.log(`⏰ [BOARD_RECOVERY] Scheduling step ${nextStep} in ${Math.ceil(delay / 1000)}s`);

    recoveryTimerRef.current = setTimeout(() => {
      // Re-check: is the board still blank?
      if (!isBoardBlank()) {
        console.log('✅ [BOARD_RECOVERY] Board recovered before escalation to step', nextStep);
        return;
      }
      setRecoveryLevel(nextStep);
      executeRecovery(nextStep);
    }, delay);
  }, [showFallback, onRefetch, onReconnectSocket, isBoardBlank, executeRecovery]);

  // ── Main health check ───────────────────────────────────────────────────

  const checkHealth = useCallback(() => {
    const blank = isBoardBlank();

    // Also update the legacy flightDataMonitor row count
    try {
      const table = document.querySelector('table');
      const domRows = table ? table.querySelectorAll('tr[class*="data-row"]').length : 0;
      updateDomDataRowCount(domRows);
    } catch {}

    if (blank) {
      // ── Board is blank ────────────────────────────────────────────────
      if (!blankDetectedAt.current) {
        blankDetectedAt.current = Date.now();

        // Log diagnostics on first detection
        const status = getComprehensiveStatus(recoveryLevel);
        const diagnostics = collectBlankDiagnostics(
          boardType, status.flightsReceived || 0, currentFlights.length, routeMountedAt.current
        );
        logBlankDiagnostics(diagnostics);
        setLastError(diagnostics.category);
      } else {
        // Rate-limited subsequent logging
        const now = Date.now();
        if (now - lastDiagnosticsAt.current > 10000) {
          lastDiagnosticsAt.current = now;
          const status = getComprehensiveStatus(recoveryLevel);
          const diag = collectBlankDiagnostics(boardType, status.flightsReceived || 0, currentFlights.length, routeMountedAt.current);
          logBlankDiagnostics(diag);
        }
      }

      // Load cached flights for potential fallback
      if (!lastKnownGood) {
        const cached = getLastKnownGood(boardType);
        if (cached && cached.flights.length > 0) {
          setLastKnownGood(cached);
        }
      }

      // Show fallback after delay (avoids flicker on slow initial load)
      const blankDuration = Date.now() - blankDetectedAt.current;
      if (!showFallback && blankDuration > FALLBACK_SHOW_DELAY_MS) {
        const status = getComprehensiveStatus(recoveryLevel);
        setShowFallback(true);
        setFallbackReason(status.blankReason);

        // Start recovery ladder if not already recovering
        if (!isRecovering.current) {
          isRecovering.current = true;
          setRecoveryLevel(1);
          executeRecovery(1);
        }
      }
    } else {
      // ── Board is healthy ──────────────────────────────────────────────
      lastHealthyAt.current = Date.now();

      // Persist current flights as last-known-good
      if (currentFlights.length > 0) {
        storeLastKnownGood(boardType, currentFlights);
      }

      // If fallback was showing, wait for stability before hiding
      if (showFallback) {
        if (!stabilityTimerRef.current) {
          console.log(`⏳ [SelfHealing] Board appears healthy (fallback showing), waiting ${STABILITY_WINDOW_MS / 1000}s for stability`);

          stabilityTimerRef.current = setTimeout(() => {
            stabilityTimerRef.current = null;

            // When fallback is showing, data rows aren't in the DOM (replaced by BoardFallback).
            // Check data availability instead of DOM to determine if recovery succeeded.
            if (currentFlights.length === 0) {
              console.log('[SelfHealing] No flight data available yet, keeping fallback');
              return;
            }

            console.log(`✅ [SelfHealing] Flight data available (${currentFlights.length} flights), hiding fallback for ${boardType}`);
            setShowFallback(false);
            setFallbackReason(BlankReason.NONE);
            setRecoveryLevel(0);
            blankDetectedAt.current = null;
            isRecovering.current = false;
            setLastKnownGood(null);
            setStableFallbackUntil(null);

            if (recoveryTimerRef.current) {
              clearTimeout(recoveryTimerRef.current);
              recoveryTimerRef.current = null;
            }
          }, STABILITY_WINDOW_MS);
        }
      } else {
        // Not in fallback, not blank — clear any lingering state
        if (blankDetectedAt.current) {
          blankDetectedAt.current = null;
          isRecovering.current = false;
          if (recoveryTimerRef.current) {
            clearTimeout(recoveryTimerRef.current);
            recoveryTimerRef.current = null;
          }
        }
      }
    }
  }, [
    boardType, currentFlights, showFallback, recoveryLevel,
    lastKnownGood, isBoardBlank, executeRecovery
  ]);

  // ── Periodic health check ──────────────────────────────────────────────
  useEffect(() => {
    const initialTimer = setTimeout(checkHealth, 3000);
    const intervalId   = setInterval(checkHealth, PROBE_CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalId);
      if (recoveryTimerRef.current)  clearTimeout(recoveryTimerRef.current);
      if (stabilityTimerRef.current) clearTimeout(stabilityTimerRef.current);
    };
  }, [checkHealth]);

  // ── Re-check when flight data changes ──────────────────────────────────
  useEffect(() => {
    if (currentFlights.length > 0) {
      const t = setTimeout(checkHealth, 500);
      return () => clearTimeout(t);
    }
  }, [currentFlights.length, checkHealth]);

  // ── Return ─────────────────────────────────────────────────────────────
  return {
    showFallback,
    fallbackReason,
    renderEpoch,
    lastKnownGood,
    recoveryLevel,
    isRecovering: isRecovering.current,

    // Manual triggers (for testing / BoardErrorBoundary)
    forceRecovery: () => {
      isRecovering.current = true;
      setRecoveryLevel(1);
      executeRecovery(1);
    },
    forceRefetch: onRefetch,
  };
}

export default useBoardSelfHealing;
